import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Breadcrumbs, CircularProgress, Button,
  Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, useTheme, alpha, Link as MuiLink
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  MonetizationOn as MoneyIcon,
  Science as ScienceIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  AccountBalance as BankIcon,
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  OpenInNew as LinkIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import * as industryApi from '../services/industry';

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  position: 'relative',
  paddingBottom: theme.spacing(1.5),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const SubsectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(4),
  color: theme.palette.text.primary,
  fontSize: '1.5rem'
}));

interface SupportEcosystemDetail {
  id: string;
  industryId: string;
  subIndustryId?: string;
  category: 'places_infrastructure' | 'events_networks' | 'funding_incentives' | 'talent_innovation';
  name: string;
  leadText: string;
  bulletPoints: string[];
  intro: string;
  details: any;
  isActive: boolean;
  displayOrder: number;
}

const SupportEcosystemDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [data, setData] = useState<SupportEcosystemDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const result = await industryApi.getSupportEcosystemDetailById(id);
        setData(result);
      } catch (error) {
        console.error('Error fetching support ecosystem detail:', error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'places_infrastructure': return <BusinessIcon sx={{ fontSize: 40 }} />;
      case 'events_networks': return <PeopleIcon sx={{ fontSize: 40 }} />;
      case 'funding_incentives': return <MoneyIcon sx={{ fontSize: 40 }} />;
      case 'talent_innovation': return <ScienceIcon sx={{ fontSize: 40 }} />;
      default: return <BusinessIcon sx={{ fontSize: 40 }} />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'places_infrastructure': return theme.palette.warning;
      case 'events_networks': return theme.palette.info;
      case 'funding_incentives': return theme.palette.success;
      case 'talent_innovation': return theme.palette.secondary;
      default: return theme.palette.primary;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!data) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Support Ecosystem Detail Not Found
        </Typography>
        <Typography variant="body1" paragraph>
          The support ecosystem information you're looking for could not be found.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const color = getCategoryColor(data.category);
  const icon = getCategoryIcon(data.category);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Typography
          color="inherit"
          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate('/home')}
        >
          Home
        </Typography>
        <Typography
          color="inherit"
          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate(-1)}
        >
          Support Ecosystem
        </Typography>
        <Typography color="text.primary">{data.name}</Typography>
      </Breadcrumbs>

      {/* Header Card */}
      <MotionCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        sx={{
          mb: 4,
          background: `linear-gradient(135deg, ${alpha(color.main, 0.1)}, ${alpha(color.dark, 0.05)})`,
          border: `2px solid ${color.main}`,
          borderRadius: 3
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                mr: 3,
                p: 2,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${color.main}, ${color.dark})`,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {data.name}
              </Typography>
              <Chip
                label={data.category.replace('_', ' & ').toUpperCase()}
                sx={{
                  bgcolor: color.main,
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>
          <Typography variant="h6" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {data.leadText}
          </Typography>
        </CardContent>
      </MotionCard>

      {/* Introduction Section */}
      {data.intro && (
        <MotionPaper
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          sx={{ p: 4, mb: 4, borderRadius: 3 }}
        >
          <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary', fontSize: '1.1rem' }}>
            {data.intro}
          </Typography>
        </MotionPaper>
      )}

      {/* Category-specific content */}
      {data.category === 'places_infrastructure' && data.details && (
        <>
          {/* Key Locations Table */}
          {data.details.keyLocations && data.details.keyLocations.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">
                <LocationIcon sx={{ mr: 1, verticalAlign: 'middle', color: color.main }} />
                Key Locations
              </SubsectionTitle>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Location Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Specialization/Advantage</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.details.keyLocations.map((location: any, idx: number) => (
                      <TableRow key={idx} hover>
                        <TableCell>{location.name}</TableCell>
                        <TableCell>
                          <Chip label={location.type} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>{location.specialization}</TableCell>
                        <TableCell>
                          {location.link && (
                            <MuiLink href={location.link} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center' }}>
                              <LinkIcon fontSize="small" />
                            </MuiLink>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MotionPaper>
          )}

          {/* Support Assets */}
          {data.details.supportAssets && data.details.supportAssets.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle', color: color.main }} />
                Support Assets
              </SubsectionTitle>
              <Grid container spacing={3}>
                {data.details.supportAssets.map((asset: any, idx: number) => (
                  <Grid item xs={12} key={idx}>
                    <Card variant="outlined" sx={{ borderColor: alpha(color.main, 0.3) }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: color.main, fontWeight: 600 }}>
                          {asset.name}
                        </Typography>
                        {asset.bulletPoints && asset.bulletPoints.map((point: string, bIdx: number) => (
                          <Box key={bIdx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
                            <Typography variant="body2" color="text.secondary">{point}</Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </MotionPaper>
          )}
        </>
      )}

      {data.category === 'events_networks' && data.details && (
        <>
          {/* Top Industry Events */}
          {data.details.topEvents && data.details.topEvents.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">
                <EventIcon sx={{ mr: 1, verticalAlign: 'middle', color: color.main }} />
                Top Industry Events
              </SubsectionTitle>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>City</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Focus</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.details.topEvents.map((event: any, idx: number) => (
                      <TableRow key={idx} hover>
                        <TableCell>{event.event}</TableCell>
                        <TableCell>{event.date}</TableCell>
                        <TableCell>{event.city}</TableCell>
                        <TableCell>
                          <Chip label={event.focus} size="small" color="info" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {event.link && (
                            <MuiLink href={event.link} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center' }}>
                              <LinkIcon fontSize="small" />
                            </MuiLink>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MotionPaper>
          )}

          {/* Associations & Networks */}
          {data.details.associations && data.details.associations.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">
                <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle', color: color.main }} />
                Associations & Networks
              </SubsectionTitle>
              <Grid container spacing={3}>
                {data.details.associations.map((association: any, idx: number) => (
                  <Grid item xs={12} key={idx}>
                    <Card variant="outlined" sx={{ borderColor: alpha(color.main, 0.3) }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: color.main, fontWeight: 600 }}>
                          {association.name}
                        </Typography>
                        {association.bulletPoints && association.bulletPoints.map((point: string, bIdx: number) => (
                          <Box key={bIdx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
                            <Typography variant="body2" color="text.secondary">{point}</Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </MotionPaper>
          )}
        </>
      )}

      {data.category === 'funding_incentives' && data.details && (
        <>
          {/* Funding Programs */}
          {data.details.fundingPrograms && data.details.fundingPrograms.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">
                <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle', color: color.main }} />
                Funding Programs
              </SubsectionTitle>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Program Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Specialization/Advantage</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Stakeholder</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.details.fundingPrograms.map((program: any, idx: number) => (
                      <TableRow key={idx} hover>
                        <TableCell>{program.name}</TableCell>
                        <TableCell>{program.specialization}</TableCell>
                        <TableCell>
                          <Chip label={program.stakeholder} size="small" color="success" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {program.link && (
                            <MuiLink href={program.link} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center' }}>
                              <LinkIcon fontSize="small" />
                            </MuiLink>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MotionPaper>
          )}

          {/* Incentives & Grants */}
          {data.details.incentives && data.details.incentives.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">
                <BankIcon sx={{ mr: 1, verticalAlign: 'middle', color: color.main }} />
                Incentives & Grants
              </SubsectionTitle>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Program Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Specialization/Advantage</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Stakeholder</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.details.incentives.map((incentive: any, idx: number) => (
                      <TableRow key={idx} hover>
                        <TableCell>{incentive.name}</TableCell>
                        <TableCell>{incentive.specialization}</TableCell>
                        <TableCell>
                          <Chip label={incentive.stakeholder} size="small" color="success" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {incentive.link && (
                            <MuiLink href={incentive.link} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center' }}>
                              <LinkIcon fontSize="small" />
                            </MuiLink>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MotionPaper>
          )}

          {/* Investor Tools */}
          {data.details.investorTools && data.details.investorTools.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">Investor Tools</SubsectionTitle>
              <Grid container spacing={3}>
                {data.details.investorTools.map((tool: any, idx: number) => (
                  <Grid item xs={12} key={idx}>
                    <Card variant="outlined" sx={{ borderColor: alpha(color.main, 0.3) }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: color.main, fontWeight: 600 }}>
                          {tool.name}
                        </Typography>
                        {tool.bulletPoints && tool.bulletPoints.map((point: string, bIdx: number) => (
                          <Box key={bIdx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
                            <Typography variant="body2" color="text.secondary">{point}</Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </MotionPaper>
          )}
        </>
      )}

      {data.category === 'talent_innovation' && data.details && (
        <>
          {/* Universities, research centers, and academies */}
          {data.details.universities && data.details.universities.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">
                <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle', color: color.main }} />
                Universities, Research Centers, and Academies
              </SubsectionTitle>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Organization Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Programs</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.details.universities.map((university: any, idx: number) => (
                      <TableRow key={idx} hover>
                        <TableCell>{university.name}</TableCell>
                        <TableCell>
                          <Chip label={university.type} size="small" color="secondary" variant="outlined" />
                        </TableCell>
                        <TableCell>{university.programs}</TableCell>
                        <TableCell>
                          {university.link && (
                            <MuiLink href={university.link} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center' }}>
                              <LinkIcon fontSize="small" />
                            </MuiLink>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MotionPaper>
          )}

          {/* Innovation hubs, accelerators, and incubators */}
          {data.details.innovationHubs && data.details.innovationHubs.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">
                <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle', color: color.main }} />
                Innovation Hubs, Accelerators, and Incubators
              </SubsectionTitle>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Organization Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Programs</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.details.innovationHubs.map((hub: any, idx: number) => (
                      <TableRow key={idx} hover>
                        <TableCell>{hub.name}</TableCell>
                        <TableCell>
                          <Chip label={hub.type} size="small" color="secondary" variant="outlined" />
                        </TableCell>
                        <TableCell>{hub.programs}</TableCell>
                        <TableCell>
                          {hub.link && (
                            <MuiLink href={hub.link} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center' }}>
                              <LinkIcon fontSize="small" />
                            </MuiLink>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MotionPaper>
          )}

          {/* Skills localization and future talent pipeline */}
          {data.details.skillsLocalization && data.details.skillsLocalization.length > 0 && (
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              sx={{ p: 4, mb: 4, borderRadius: 3 }}
            >
              <SubsectionTitle variant="h5">Skills Localization and Future Talent Pipeline</SubsectionTitle>
              <Grid container spacing={3}>
                {data.details.skillsLocalization.map((asset: any, idx: number) => (
                  <Grid item xs={12} key={idx}>
                    <Card variant="outlined" sx={{ borderColor: alpha(color.main, 0.3) }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: color.main, fontWeight: 600 }}>
                          {asset.name}
                        </Typography>
                        {asset.bulletPoints && asset.bulletPoints.map((point: string, bIdx: number) => (
                          <Box key={bIdx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
                            <Typography variant="body2" color="text.secondary">{point}</Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </MotionPaper>
          )}
        </>
      )}

      {/* Back Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            borderColor: color.main,
            color: color.main,
            '&:hover': {
              borderColor: color.dark,
              bgcolor: alpha(color.main, 0.1)
            }
          }}
        >
          Back to Industry Page
        </Button>
      </Box>
    </Container>
  );
};

export default SupportEcosystemDetailPage;
